// Role-based access control for navigation and routes

export type AppRole =
  | 'Admin' | 'Manager' | 'Gerente' | 'DireccionTecnica'
  | 'VendedorMarcaPropia' | 'Produccion' | 'Calidad'
  | 'Seller' | 'User' | 'ReadOnly'
  // Roles minoristas (POS)
  | 'Propietario' | 'Empleado' | 'EmpleadoPlus' | 'Cajero';

export interface RolePermission {
  pages: string[];
}

const ALL_PAGES = [
  'dashboard', 'calendar', 'pos', 'caja', 'products', 'inventory', 'production-orders', 'retention-samples',
  'sales', 'sales/import', 'analytics', 'channels', 'ml-mapping',
  'customers', 'customers/import', 'prospects', 'prospects/import',
  'activities', 'opportunities', 'opportunities/import',
  'replenishment', 'reports', 'recipes', 'prices', 'materials',
  'services', 'insumos', 'suppliers', 'purchases', 'purchase-requests', 'debt-ranking', 'sellers',
  'users', 'settings', 'cash-flow', 'purchase-suggestions', 'expenses', 'ai',
  'today', 'lot-profitability', 'mobile',
  'commercial-dashboard', 'price-lists', 'uninvoiced', 'channel-profitability', 'production-plan', 'commercial-settings', 'import-spreadsheet',
  'factory-production', 'factory-costs', 'factory-outwork', 'factory-customer-orders', 'factory-debts',
  'factory-stock', 'factory-storage', 'factory-maintenance', 'factory-materials'
];

const SELLER_PAGES = [
  'dashboard', 'calendar', 'pos', 'sales', 'opportunities', 'activities',
  'customers', 'inventory', 'replenishment', 'cash-flow', 'reports',
  'prospects', 'today', 'mobile'
];

// Con la bandera "seller-extended-access" del tenant, el vendedor (rol Seller) ve ademas
// Comercial, Catalogo, Operaciones, Inteligencia y Abastecimiento. El backend lo deja
// solo en lectura (SellerAccessMiddleware).
export const SELLER_EXTENDED_FLAG = 'seller-extended-access';
const SELLER_EXTENDED_PAGES = [
  ...SELLER_PAGES,
  'caja', 'sellers',
  'products', 'materials', 'services', 'insumos', 'recipes', 'prices',
  'stock-movements', 'production-orders', 'retention-samples',
  'purchase-requests', 'purchases', 'debt-ranking', 'suppliers',
  'ai', 'analytics', 'purchase-suggestions'
];

const PRODUCTION_PAGES = [
  'products', 'materials', 'services', 'recipes', 'prices',
  'production-orders', 'retention-samples', 'insumos', 'inventory', 'purchase-requests',
  'factory-production', 'factory-outwork', 'factory-stock', 'factory-storage', 'factory-maintenance', 'factory-materials'
];

const READONLY_PAGES = [
  'dashboard', 'calendar', 'sales', 'customers', 'inventory',
  'reports', 'analytics', 'cash-flow'
];

// Empleado: opera el POS y vende, SIN acceso a la caja.
// Puede cargar pedidos internos ("hace falta comprar X") para quien compra.
const EMPLEADO_PAGES = [
  'dashboard', 'pos', 'sales', 'customers', 'products', 'inventory', 'purchase-requests'
];
// Empleado plus: igual que empleado pero VE LA CAJA.
const EMPLEADO_PLUS_PAGES = [...EMPLEADO_PAGES, 'caja'];
// Cajero: enfocado en POS + caja.
const CAJERO_PAGES = [
  'dashboard', 'pos', 'caja', 'sales', 'customers'
];

export const ROLE_PERMISSIONS: Record<string, RolePermission> = {
  Admin: { pages: ALL_PAGES },
  Manager: { pages: ALL_PAGES },
  Gerente: { pages: ALL_PAGES },
  Propietario: { pages: ALL_PAGES },
  DireccionTecnica: { pages: ALL_PAGES },
  VendedorMarcaPropia: { pages: SELLER_PAGES },
  Seller: { pages: SELLER_PAGES },
  User: { pages: SELLER_PAGES },
  Produccion: { pages: PRODUCTION_PAGES },
  Calidad: { pages: PRODUCTION_PAGES },
  ReadOnly: { pages: READONLY_PAGES },
  Empleado: { pages: EMPLEADO_PAGES },
  EmpleadoPlus: { pages: EMPLEADO_PLUS_PAGES },
  Cajero: { pages: CAJERO_PAGES },
};

export function hasPageAccess(role: string | undefined, page: string, sellerExtended = false): boolean {
  if (!role) return false;
  if (sellerExtended && role.toLowerCase() === 'seller') return SELLER_EXTENDED_PAGES.includes(page);
  // Try exact match first, then case-insensitive search
  const perm = ROLE_PERMISSIONS[role]
    || Object.entries(ROLE_PERMISSIONS).find(([k]) => k.toLowerCase() === role.toLowerCase())?.[1];
  // Unknown roles fall back to seller-level access so authenticated users
  // are never left with an empty menu.
  if (!perm) return SELLER_PAGES.includes(page);
  return perm.pages.includes(page);
}

export function getDefaultRoute(role: string | undefined): string {
  if (!role) return '/login';
  const r = role.toLowerCase();
  if (r === 'produccion' || r === 'calidad') return '/products';
  if (r === 'readonly') return '/dashboard';
  if (r === 'cajero' || r === 'empleado' || r === 'empleadoplus') return '/pos';
  return '/dashboard';
}

// Navigation sections with role filtering
export interface NavSection {
  title: string;
  items: { id: string; label: string; path: string; icon: string }[];
}

export function getNavSections(role: string): NavSection[] {
  const check = (page: string) => hasPageAccess(role, page);

  const sections: NavSection[] = [];

  // Comercial
  const comercial = {
    title: 'Comercial',
    items: [
      check('dashboard') && { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      check('calendar') && { id: 'calendar', label: 'Calendario', path: '/calendar', icon: 'CalendarMonth' },
      check('opportunities') && { id: 'opportunities', label: 'Oportunidades', path: '/opportunities', icon: 'TrendingUp' },
      check('sales') && { id: 'sales', label: 'Ventas', path: '/sales', icon: 'PointOfSale' },
      check('customers') && { id: 'customers', label: 'Clientes', path: '/customers', icon: 'People' },
      check('prospects') && { id: 'prospects', label: 'Prospectos', path: '/prospects', icon: 'PersonSearch' },
      check('sellers') && { id: 'sellers', label: 'Vendedores', path: '/sellers', icon: 'Badge' },
      check('activities') && { id: 'activities', label: 'Actividades', path: '/activities', icon: 'Assignment' },
    ].filter(Boolean) as NavSection['items'],
  };
  if (comercial.items.length) sections.push(comercial);

  // Producción
  const produccion = {
    title: 'Producción',
    items: [
      check('products') && { id: 'products', label: 'Productos', path: '/products', icon: 'Inventory2' },
      check('materials') && { id: 'materials', label: 'Materias Primas', path: '/materials', icon: 'Category' },
      check('insumos') && { id: 'insumos', label: 'Insumos', path: '/insumos', icon: 'Build' },
      check('services') && { id: 'services', label: 'Servicios', path: '/services', icon: 'MiscellaneousServices' },
      check('recipes') && { id: 'recipes', label: 'Formulaciones', path: '/recipes', icon: 'Receipt' },
      check('prices') && { id: 'prices', label: 'Precios', path: '/prices', icon: 'PriceCheck' },
      check('production-orders') && { id: 'production-orders', label: 'Órdenes de Producción', path: '/production-orders', icon: 'Factory' },
    ].filter(Boolean) as NavSection['items'],
  };
  if (produccion.items.length) sections.push(produccion);

  // Inventario & Compras
  const inventario = {
    title: 'Inventario & Compras',
    items: [
      check('inventory') && { id: 'inventory', label: 'Inventario', path: '/inventory', icon: 'Warehouse' },
      check('replenishment') && { id: 'replenishment', label: 'Reabastecimiento', path: '/replenishment', icon: 'ShoppingCart' },
      check('suppliers') && { id: 'suppliers', label: 'Proveedores', path: '/suppliers', icon: 'LocalShipping' },
      check('purchases') && { id: 'purchases', label: 'Compras', path: '/purchases', icon: 'Receipt' },
    ].filter(Boolean) as NavSection['items'],
  };
  if (inventario.items.length) sections.push(inventario);

  // Reportes & Finanzas
  const reportes = {
    title: 'Reportes & Finanzas',
    items: [
      check('reports') && { id: 'reports', label: 'Reportes', path: '/reports', icon: 'Assessment' },
      check('analytics') && { id: 'analytics', label: 'Analíticas', path: '/analytics', icon: 'Analytics' },
      check('cash-flow') && { id: 'cash-flow', label: 'Flujo de Caja', path: '/cash-flow', icon: 'AccountBalance' },
    ].filter(Boolean) as NavSection['items'],
  };
  if (reportes.items.length) sections.push(reportes);

  // Admin
  const admin = {
    title: 'Administración',
    items: [
      check('channels') && { id: 'channels', label: 'Canales', path: '/channels', icon: 'Hub' },
      check('users') && { id: 'users', label: 'Usuarios', path: '/users', icon: 'ManageAccounts' },
      check('settings') && { id: 'settings', label: 'Configuración', path: '/settings', icon: 'Settings' },
    ].filter(Boolean) as NavSection['items'],
  };
  if (admin.items.length) sections.push(admin);

  return sections;
}
